// Prerender-safe, lazy GA4 funnel-event helper.
//
// CRA prerenders pages at build time (no `window`), so analytics must never run
// at module load. This helper lazy-imports `firebase/analytics` only when an event
// fires, guards `window`, awaits `isSupported()`, caches the instance, and is
// strictly best-effort — it never throws, even if analytics is blocked/unsupported.
//
// GA4 is linked to the LOCAL project (booker-1d469); `measurementId` lives on the
// local Firebase app config in `./config`.

import localApp from './config';

let analyticsPromise = null;

// Resolve (and cache) the analytics instance, or null if unavailable.
function getAnalyticsInstance() {
  if (typeof window === 'undefined') return Promise.resolve(null); // prerender guard
  if (analyticsPromise) return analyticsPromise;

  analyticsPromise = import('firebase/analytics')
    .then(async ({ getAnalytics, isSupported }) => {
      const supported = await isSupported();
      if (!supported) return null;
      return getAnalytics(localApp);
    })
    .catch(() => null);

  return analyticsPromise;
}

/**
 * Fire a GA4 event. Best-effort: silently no-ops on prerender, unsupported
 * environments, or any failure.
 * @param {string} name - GA4 event name (e.g. 'demo_cta_click').
 * @param {object} [params] - Event params (e.g. { demo: 'booking' }).
 */
export async function logFunnelEvent(name, params = {}) {
  try {
    const instance = await getAnalyticsInstance();
    if (!instance) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(instance, name, params);
  } catch (_) {
    // best-effort: never throw from analytics
  }
}
